<template>
  <section
    ref="homepageRef"
    class="Homepage"
    @scroll.passive="handleScroll"
  >
    <section
      ref="heroRef"
      class="Homepage__hero"
    >
      <figure
        class="Homepage__heroFigure"
      >
        <TeaserPageCanvas
          v-if="!isTest"
          class="Homepage__heroFigureCanvas"
        />
      </figure>

      <header class="Homepage__heroHeader">
        <CalloutTitle
          class="Homepage__heroTitle"
          size="Inherit"
          :title="`Make more\nof your money`"
          :callouts="['more', 'money']"
        />
        <UIText
          class="Homepage__heroBody"
          tag="p"
          size="Small"
          isMultiline
        >
          Era learns your financial habits and automates the manual work, while you maintain visibility and control.
        </UIText>

        <div class="Homepage__heroButtonContainer">
          <FormButton
            variant="Primary"
            data-testid="signupButton"
            isRounded
            @click="isSigningUp = true"
          >
            Sign up for free
          </FormButton>

          <FormButton
            variant="Secondary"
            data-testid="loginButton"
            isRounded
            @click="isLoggingIn = true"
          >
            Sign in
          </FormButton>
        </div>

        <Transition>
          <div
            v-show="!isHeroVisible"
            class=" Homepage__heroButtonContainer Homepage__heroButtonContainer--sticky"
          >
            <FormButton
              variant="Primary"
              data-testid="signupButton"
              isRounded
              @click="isSigningUp = true"
            >
              Sign up for free
            </FormButton>

            <FormButton
              variant="Secondary"
              data-testid="loginButton"
              isRounded
              @click="isLoggingIn = true"
            >
              Sign in
            </FormButton>
          </div>
        </Transition>
      </header>
    </section>

    <div class="Homepage__wrapper Homepage__featureSection">
      <HomepageChatFeatureSection class="Homepage__featureSectionCard" />
      <HomepageRoutinesFeatureSection class="Homepage__featureSectionCard" />
      <HomepageTransfersFeatureSection class="Homepage__featureSectionCard" />
      <HomepageAccountsFeatureSection class="Homepage__featureSectionCard" />
    </div>

    <footer
      ref="footerRef"
      class="Homepage__footer"
    >
      <div class="Homepage__wrapper">
        <ul class="Homepage__footerLinkList">
          <li
            v-for="link in footerLinks"
            :key="link.url"
            class="Homepage__footerLinkItem"
          >
            <UIText
              class="Homepage__footerLink"
              tag="a"
              size="Small"
              :href="link.url"
            >
              {{ link.title }}
            </UIText>
          </li>
        </ul>

        <div class="Homepage__footerLogoContainer">
          <Logo class="Homepage__footerLogo" />

          <div class="Homepage__footerBadgeContainer">
            <div class="Homepage__footerBadgeList">
              <AppStoreBadge />
              <PlayStoreBadge />
            </div>

            <UIText
              class="Homepage__copyright"
              size="Small"
              tag="div"
            >
              &copy; 2025 Tinwell Labs Inc. DBA Era
            </UIText>
          </div>
        </div>
      </div>
    </footer>

    <Teleport :to="PortalId.Body">
      <Transition>
        <SheetModal
          v-if="isLoggingIn || isSigningUp"
          data-testid="authModal"
          size="Hug"
          theme="Light"
          :canClose="false"
          @close="handleClose"
        >
          <AuthFormView
            :variant="isLoggingIn ? 'Login' : 'Signup'"
          />
        </SheetModal>
      </Transition>
    </Teleport>
  </section>
</template>

<script lang="ts" setup>
import CalloutTitle from "@cosine/components/CalloutTitle.vue";
import FormButton from "@cosine/components/FormButton.vue";
import Logo from "@cosine/components/Logo.vue";
import { PortalId } from "@cosine/components/Portal.types";
import SheetModal from "@cosine/components/SheetModal.vue";
import UIText from "@cosine/components/UIText.vue";
import AuthFormView from "@cosine/views/auth/AuthFormView.vue";
import { onMounted, onBeforeUnmount, useTemplateRef, ref, computed } from "vue";
import HomepageChatFeatureSection from "@cosine/components/homepage/HomepageChatFeatureSection.vue";
import HomepageRoutinesFeatureSection from "@cosine/components/homepage/HomepageRoutinesFeatureSection.vue";
import HomepageTransfersFeatureSection from "@cosine/components/homepage/HomepageTransfersFeatureSection.vue";
import HomepageAccountsFeatureSection from "@cosine/components/homepage/HomepageAccountsFeatureSection.vue";
import TeaserPageCanvas from "@cosine/components/onboarding/welcome/TeaserPageCanvas.vue";
import useRuntimeEnvironment from "@shared/composables/config/useRuntimeEnvironment";
import { onBeforeRouteLeave } from "vue-router";
import { RouteName } from "@cosine/routes";
import useRedirectQueryParam from "@cosine/composables/useRedirectQueryParam";
import useFullSeoMeta from "@cosine/composables/useFullSeoMeta";
import AppStoreBadge from "@cosine/components/AppStoreBadge.vue";
import PlayStoreBadge from "@cosine/components/PlayStoreBadge.vue";
import { useHead } from "@unhead/vue";

const {
  isTest,
  wwwBaseUrl,
} = useRuntimeEnvironment();

useFullSeoMeta({
  title: "AI to make more of your money",
  description: "Unlock the power of AI-driven personal finance management with Era. Take control of your finances effortlessly, optimize investments, and maximize savings. Let Era guide you through key financial terms, making every dollar count. Experience the future of wealth management today.",
});

useHead({
  meta: [
    {
      name: "robots",
      content: "index, follow",
    },
  ],
});

useRedirectQueryParam(RouteName.index);

const homepageRef = useTemplateRef("homepageRef");
const heroRef = useTemplateRef("heroRef");
const footerRef = useTemplateRef("footerRef");
const footerVisibleHeight = ref(0);
const scrollRafId = ref<number | null>(null);
const isHeroVisible = ref(true);

const isLoggingIn = ref(false);
const isSigningUp = ref(false);

const headerHeight = 105;
const scrollbarWidth = ref(0);

const footerLinks = computed(() => {
  return [
    {
      title: "Support",
      url: `${wwwBaseUrl}/support`,
    },
    {
      title: "Terms of service",
      url: `${wwwBaseUrl}/legal/terms-of-service`,
    },
    {
      title: "Privacy policy",
      url: `${wwwBaseUrl}/legal/privacy-policy`,
    },
    {
      title: "LinkedIn",
      url: "https://www.linkedin.com/company/financial-assistant",
    },
    {
      title: "X (Twitter)",
      url: "https://x.com/mymoneyera",
    },
    {
      title: "TikTok",
      url: "https://www.tiktok.com/@mymoneyera",
    },
  ];
});

onMounted(() => {
  updateFooterVisibleHeight();
  updateHeroVisibility();

  window.addEventListener("resize", handleResize, {
    passive: true,
  });

  handleResize();
});

onBeforeUnmount(() => {
  if (scrollRafId.value) {
    cancelAnimationFrame(scrollRafId.value);
  }

  window.removeEventListener("resize", handleResize);
});

onBeforeRouteLeave((to) => {
  if (to.name === RouteName.login) {
    isLoggingIn.value = true;
    return false;
  }
});

function updateFooterVisibleHeight () {
  if (!footerRef.value) return;

  const footerRect = footerRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  if (footerRect.top >= viewportHeight) {
    footerVisibleHeight.value = 0;
  } else if (footerRect.bottom <= 0) {
    footerVisibleHeight.value = 0;
  } else {
    const visibleTop = Math.max(0, footerRect.top);
    const visibleBottom = Math.min(viewportHeight, footerRect.bottom);
    footerVisibleHeight.value = Math.max(0, visibleBottom - visibleTop);
  }
}

function updateHeroVisibility () {
  if (!heroRef.value) return;

  const heroRect = heroRef.value.getBoundingClientRect();
  isHeroVisible.value = heroRect.bottom > headerHeight + 24;
}

function handleResize () {
  const windowWidth = window.innerWidth;
  const scrollAreaWidth = (
    windowWidth < 600
      ? homepageRef.value?.clientWidth
      : homepageRef.value?.closest(".AppLayout")?.clientWidth
  ) || windowWidth;
  scrollbarWidth.value = window.innerWidth - scrollAreaWidth;

  handleScroll();
}

function handleScroll () {
  if (scrollRafId.value) {
    cancelAnimationFrame(scrollRafId.value);
  }

  scrollRafId.value = requestAnimationFrame(() => {
    updateFooterVisibleHeight();
    updateHeroVisibility();
  });
}

function handleClose () {
  isLoggingIn.value = false;
  isSigningUp.value = false;
}
</script>

<style lang="scss" scoped>
@function stripUnit($px) {
  @return calc($px / ($px * 0 + 1px)); }

@function scaleWithViewport($minValue, $maxValue, $minWidth: 375px, $maxWidth: 1100px) {
  $v: calc((100 * ($maxValue - $minValue)) / ($maxWidth - $minWidth));
  $r: calc((($minWidth * $maxValue) - ($maxWidth * $minValue)) / ($minWidth - $maxWidth));

  @return clamp($minValue, $v * 1vw + stripUnit($r) * 1px, $maxValue);
}

.Homepage {
  --viewportWidth: calc(100vw - v-bind(scrollbarWidth) * 1px);
  --headerHeight: 105px;
  --headerInlinePadding: #{scaleWithViewport(16px, 64px, 375px, 600px)};
  --buttonContainerHeight: 236px;
  display: grid;

  @media (max-width: 599px) {
    height: 100%;
    overflow-y: auto;
  }
}

.Homepage__wrapper {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
}

.Homepage__hero {
  --headerMaxWidth: 532px;
  position: relative;

  display: grid;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;

  @media (min-width: 1100px) {
    grid-template-columns: max-content auto;
  }
}

.Homepage__heroHeader {
  display: grid;
  grid-auto-rows: min-content;
  gap: 16px;
  max-width: var(--headerMaxWidth);
  padding: 24px var(--layoutMargin) 24px;

  @media (max-width: 599px) {
    padding-inline: var(--headerInlinePadding);
    padding-bottom: scaleWithViewport(24px, 64px, 375px, 600px);
  }

  @media (min-width: 600px) and (max-width: 1099px) {
    margin: 0 auto;
    padding-top: scaleWithViewport(48px, 80px, 600px, 1100px);
    padding-bottom: 24px;

    text-align: center;
  }

  @media (min-width: 1100px) {
    padding-inline: 0;
    padding-block: scaleWithViewport(80px, 120px, 1100px, 1250px);
  }
}

.Homepage__heroTitle {
  --backgroundColor: var(--colorSwissPurple300);
  font-size: scaleWithViewport(48px, 75px, 375px, 900px);
}

.Homepage__heroBody {
  text-wrap: pretty;
}

.Homepage__heroFigure {
  position: relative;

  @media (max-width: 600px) {
    margin-bottom: -16px;
    mask: linear-gradient(
      to bottom,
      transparent 0,
      black 0,
      black calc(100% - 16px),
      transparent 100%,
    );
  }

  @media (min-width: 600px) and (max-width: 1099px){
    order: 1;
    mask: linear-gradient(
      to bottom,
      transparent 0,
      black 24px,
      black 100%,
      transparent 100%,
    );
  }

  @media (max-width: 1099px) {
    width: 100%;
    max-height: 500px;
    aspect-ratio: 375 / 250;
    overflow: hidden;
  }

  @media (min-width: 1100px) {
    order: 1;
  }
}

.Homepage__heroFigureCanvas {
  position: absolute;
  inset: 0;

  @media (min-width: 1100px) {
    width: min(650px, calc(900px - var(--headerMaxWidth) + (var(--viewportWidth) - 900px) / 2));
  }
}

.Homepage__heroButtonContainer {
  --footerVisibleHeight: calc(1px * v-bind(footerVisibleHeight));

  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 8px;
  max-width: 360px;
  margin-top: 8px;

  @media (max-width: 449px) {
    grid-template-columns: 1fr;
  }

  @media (min-width: 600px) and (max-width: 1099px) {
    margin-inline: auto;
  }
}

.Homepage__heroButtonContainer--sticky {
  @media (max-width: 449px) {
    position: fixed;
    left: var(--headerInlinePadding);
    right: var(--headerInlinePadding);
    bottom: calc(var(--headerInlinePadding) + var(--footerVisibleHeight));
    z-index: 1;
  }

  &.v-enter-active,
  &.v-leave-active {
    transition: 150ms ease-out;
    transition-property: opacity, transform;
  }

  &.v-enter-from,
  &.v-leave-to {
    transform: translateY(100%);
    opacity: 0;
  }

  &.v-enter-to,
  &.v-leave-from {
    transform: translateY(0);
    opacity: 1;
  }
}

.Homepage__featureSection {
  display: grid;

  @media (min-width: 900px) {
    gap: 48px;
    padding: 64px 0;
  }
}

.Homepage__featureSectionCard {
  min-height: 412px;

  @media (min-height: 750px) {
    position: sticky;
    top: 0;
  }

  @media (max-width: 449px) {
    padding-bottom: calc(var(--buttonContainerHeight) - 24px);
  }

  @media (min-width: 900px) {
    top: calc(var(--headerHeight) + 40px);
  }
}

.Homepage__footer {
  display: grid;
  padding-block: scaleWithViewport(24px, 80px);

  background-color: var(--colorSwissBlack);
  color: var(--colorWhite);

  @media (max-width: 449px) {
    padding-inline: var(--layoutMargin);
  }

  @media (min-width: 900px) {
    padding-inline: var(--layoutMargin);
  }
}

.Homepage__footerLinkList,
.Homepage__footerLogoContainer {
  @media (min-width: 450px) {
    padding: 0 48px;
  }
}

.Homepage__footerLinkList {
  column-count: 2;
  column-gap: 0;
}

.Homepage__footerLinkItem {
  margin-bottom: 48px;
}

.Homepage__footerLink {
  text-decoration: none;
}

.Homepage__footerLogoContainer {
  display: grid;
  gap: 32px 0;
  align-items: end;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.Homepage__footerLogo {
  --logoFill: currentColor;
  width: 50%;
}

.Homepage__footerBadgeContainer {
  display: grid;
  gap: 12px;
}

.Homepage__footerBadgeList {
  display: grid;
  grid-template-columns: min-content min-content;
  gap: 12px;
}
</style>
